/**
 * input.js -- keyboard + Gamepad API -> libretro joypad state, plus
 * user-remappable gamepad bindings, host-level "actions" (currently just
 * fast forward) that live alongside the 12 SNES buttons but never reach the
 * core, and a "modifier" system for giving gamepad buttons double duty.
 *
 * Both keyboard and gamepad write into the same per-port button table and
 * push changes into the core via core.setInput(). Keyboard is event-driven;
 * gamepads have no event model for button state, so they are polled once per
 * frame from the emulator loop (pollGamepads()).
 *
 * Modifier system: one physical gamepad control (button or axis, same
 * binding-value representation as everything else here) can be designated
 * "the modifier". While it's held, any OTHER already-bound SNES button that
 * has a "modified binding" configured stops sending its normal SNES press
 * and instead fires a one-shot host action (Save State, Fullscreen, ...) or
 * plays back a recorded MACRO -- a literal recording of a button-press
 * sequence (startRecordingMacro()/stopRecordingMacro()), played back through
 * a fourth input source (macroState) merged into _flushPort() exactly like
 * keyboard/gamepad/touch. The modifier's own control is never suppressed by
 * itself -- pressing it alone still sends its normal SNES press if it has
 * one; only *other* controls change behaviour while it's held.
 */

/** RETRO_DEVICE_ID_JOYPAD_* -- must match libretro.h exactly. */
export const JOYPAD = {
  B: 0,
  Y: 1,
  SELECT: 2,
  START: 3,
  UP: 4,
  DOWN: 5,
  LEFT: 6,
  RIGHT: 7,
  A: 8,
  X: 9,
  L: 10,
  R: 11,
};

/**
 * Default keyboard map for player 1, using KeyboardEvent.code so it is
 * layout-independent (WASD stays where W/A/S/D physically are on AZERTY).
 *
 * The face-button layout mirrors the physical SNES pad: X/Y on the left
 * column, A/B on the right.
 */
export const DEFAULT_KEYMAP = {
  ArrowUp: JOYPAD.UP,
  ArrowDown: JOYPAD.DOWN,
  ArrowLeft: JOYPAD.LEFT,
  ArrowRight: JOYPAD.RIGHT,
  KeyZ: JOYPAD.B,
  KeyX: JOYPAD.A,
  KeyA: JOYPAD.Y,
  KeyS: JOYPAD.X,
  KeyQ: JOYPAD.L,
  KeyW: JOYPAD.R,
  Enter: JOYPAD.START,
  ShiftRight: JOYPAD.SELECT,
  ShiftLeft: JOYPAD.SELECT,
};

/** Keyboard keys that drive a held "action" (see ACTIONS) rather than a SNES button. */
const ACTION_KEYMAP = {
  Tab: 'fastForward',
};

/**
 * Host-level actions a gamepad button (or the keyboard, via ACTION_KEYMAP)
 * can be bound to. These never reach the core -- the emulator loop reads
 * them via isActionHeld() -- so they can share index space with SNES button
 * bindings without colliding in the UI.
 */
export const ACTIONS = [{ name: 'fastForward', label: 'Fast forward (hold)' }];

/**
 * One-shot host actions a modifier+button combo can trigger. Deliberately
 * excludes `fastForward`: that's a *held* action (isActionHeld()), which
 * doesn't make sense for an edge-triggered "combo just pressed" event the
 * way a save/load/menu action does. Names match HOTKEYS' values 1:1 so
 * modifier-triggered actions dispatch through the exact same onHotkey
 * callback as keyboard shortcuts -- see main.js's handleHotkey().
 */
export const MODIFIABLE_ACTIONS = [
  { name: 'saveState', label: 'Save state' },
  { name: 'loadState', label: 'Load state' },
  { name: 'nextSlot', label: 'Next save slot' },
  { name: 'prevSlot', label: 'Previous save slot' },
  { name: 'pause', label: 'Pause / resume' },
  { name: 'reset', label: 'Reset' },
  { name: 'mute', label: 'Mute / unmute' },
  { name: 'fullscreen', label: 'Fullscreen' },
];

export const JOYPAD_ID_TO_NAME = Object.fromEntries(Object.entries(JOYPAD).map(([name, id]) => [id, name]));

/** SNES buttons offered in the remap UI, in on-screen order. */
export const REMAPPABLE_BUTTONS = [
  { name: 'UP', label: 'D-pad Up' },
  { name: 'DOWN', label: 'D-pad Down' },
  { name: 'LEFT', label: 'D-pad Left' },
  { name: 'RIGHT', label: 'D-pad Right' },
  { name: 'A', label: 'A' },
  { name: 'B', label: 'B' },
  { name: 'X', label: 'X' },
  { name: 'Y', label: 'Y' },
  { name: 'L', label: 'L' },
  { name: 'R', label: 'R' },
  { name: 'START', label: 'Start' },
  { name: 'SELECT', label: 'Select' },
];

/**
 * Default gamepad bindings, keyed by SNES button / action name -> standard
 * Gamepad API button index. The browser's "standard mapping" puts
 * buttons[0..3] at the bottom/right/left/top face positions (Xbox A/B/X/Y),
 * which maps onto the SNES diamond as below. Indices 6/7 (the analog
 * triggers) are left free by the face/shoulder buttons, so fast forward
 * defaults to the right trigger -- the same slot most emulators use for it.
 */
const DEFAULT_BUTTON_BINDINGS = {
  B: 0,
  A: 1,
  Y: 2,
  X: 3,
  L: 4,
  R: 5,
  SELECT: 8,
  START: 9,
  UP: 12,
  DOWN: 13,
  LEFT: 14,
  RIGHT: 15,
};

const DEFAULT_ACTION_BINDINGS = {
  fastForward: 7,
};

const GAMEPAD_MAP_STORAGE_KEY = 'snes9x-web:gamepad-map';
// Separate from GAMEPAD_MAP_STORAGE_KEY on purpose: "Reset to defaults" in
// the Gamepad mapping panel only touches button/action bindings, not a
// carefully-recorded macro -- that needs its own explicit reset.
const MODIFIER_STORAGE_KEY = 'snes9x-web:modifier-map';

const AXIS_DEADZONE = 0.5;

/**
 * A binding value is either a gamepad BUTTON index (number) or an AXIS
 * binding encoded as the string "a<axisIndex><sign>", e.g. "a2+" / "a3-".
 * This exists because some controllers -- generic/budget ones especially,
 * whose analog triggers don't get recognised as the "standard" Gamepad API
 * layout -- report their triggers only through pad.axes[], never
 * pad.buttons[], so a purely button-index-based remap system can never
 * capture or bind them. Old persisted bindings are always plain numbers, so
 * this stays backwards compatible with no migration needed.
 */
function isAxisBinding(value) {
  return typeof value === 'string';
}
function parseAxisBinding(value) {
  const sign = value.slice(-1);
  return { axisIndex: Number(value.slice(1, -1)), sign };
}
function encodeAxisBinding(axisIndex, sign) {
  return `a${axisIndex}${sign}`;
}
/** Human-readable label for a binding value, for the remap UI. */
export function describeBinding(value) {
  if (value === null || value === undefined) return null;
  if (isAxisBinding(value)) {
    const { axisIndex, sign } = parseAxisBinding(value);
    return `axis ${axisIndex} (${sign})`;
  }
  return `button ${value}`;
}

/**
 * Object keys are always strings, even when set with a numeric key (as
 * InputManager.extraBindings is, via setExtraAction/setExtraMacro) -- so
 * Object.keys(extraBindings) hands back "5" for a button index, not 5. Since
 * isAxisBinding()/describeBinding() key off `typeof value === 'string'` to
 * tell a button index apart from an axis binding ("a3+"), a numeric-looking
 * string has to be converted back to a real number first or it's
 * misidentified as an axis binding. Axis-binding strings are left as-is.
 */
export function parseExtraKey(key) {
  return /^-?\d+$/.test(key) ? Number(key) : key;
}

function loadStoredBindings() {
  try {
    const raw = localStorage.getItem(GAMEPAD_MAP_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      buttons: { ...DEFAULT_BUTTON_BINDINGS, ...(parsed.buttons ?? {}) },
      actions: { ...DEFAULT_ACTION_BINDINGS, ...(parsed.actions ?? {}) },
    };
  } catch {
    return null; // corrupt or inaccessible storage: fall back to defaults
  }
}

function loadStoredModifierConfig() {
  try {
    const raw = localStorage.getItem(MODIFIER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      modifier: parsed.modifier ?? null,
      modified: parsed.modified && typeof parsed.modified === 'object' ? parsed.modified : {},
      extra: parsed.extra && typeof parsed.extra === 'object' ? parsed.extra : {},
    };
  } catch {
    return null;
  }
}

export class InputManager {
  /**
   * @param {import('./core.js').Snes9xCore} core
   * @param {{ onHotkey?: (name: string, event: KeyboardEvent) => void }} options
   */
  constructor(core, { onHotkey } = {}) {
    this.core = core;
    this.onHotkey = onHotkey;
    this.keymap = { ...DEFAULT_KEYMAP };

    const stored = loadStoredBindings();
    this.buttonBindings = stored?.buttons ?? { ...DEFAULT_BUTTON_BINDINGS };
    this.actionBindings = stored?.actions ?? { ...DEFAULT_ACTION_BINDINGS };

    const modifierConfig = loadStoredModifierConfig();
    this.modifierBinding = modifierConfig?.modifier ?? null; // a binding value (number|string), or null if unset
    this.modifiedBindings = modifierConfig?.modified ?? {}; // SNES button name -> {type:'action', action} | {type:'macro', events, label}
    // Same shape as modifiedBindings, but keyed by a raw binding value (a
    // button index or axis string, same representation as modifierBinding/
    // buttonBindings) instead of an SNES button name -- for a modifier combo
    // on a physical control that isn't mapped to any SNES button at all
    // (e.g. a spare stick button no game control needs). See setExtraAction/
    // setExtraMacro.
    this.extraBindings = modifierConfig?.extra ?? {};

    this._rebuildLookups();

    // Current state per port, so keyboard/gamepad/on-screen touch
    // controls/macro playback can be merged without one source clearing
    // another's presses.
    this.keyboardState = new Map(); // buttonId -> bool  (port 0)
    this.gamepadState = new Map(); // `${port}:${buttonId}` -> bool
    this.touchState = new Map(); // buttonId -> bool  (port 0, from touch-controls.js)
    this.macroState = new Map(); // buttonId -> bool  (port 0, from a playing-back macro)
    this.lastSent = new Map(); // `${port}:${buttonId}` -> bool

    this._keyHeldActions = new Set(); // actions held via the keyboard
    this._heldActions = new Set(); // actions held via the primary gamepad
    this._touchHeldActions = new Set(); // actions held via on-screen touch controls
    this._prevRaw = []; // primary gamepad's raw button.pressed[] last frame
    this._prevAxes = []; // primary gamepad's raw axes[] last frame
    this._prevComboRaw = new Map(); // physical index -> bool, for modifier-combo edge detection while the normal button send is suppressed

    this._listenTarget = null; // { kind: 'button'|'action'|'modifier', name?: string }
    this._listenResolve = null;
    this._listenTimer = null;

    this._recording = null; // { startTime, events: [] } while startRecordingMacro() is active
    this._macroTimers = []; // pending setTimeout ids from the most recent playMacro()

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onKeyUp = this._onKeyUp.bind(this);
    this._onBlur = this._onBlur.bind(this);
  }

  attach(target = window) {
    this.target = target;
    target.addEventListener('keydown', this._onKeyDown);
    target.addEventListener('keyup', this._onKeyUp);
    // Without this, holding a direction and alt-tabbing leaves the key stuck
    // down forever -- the keyup is delivered to whatever took focus.
    window.addEventListener('blur', this._onBlur);
  }

  detach() {
    if (!this.target) return;
    this.target.removeEventListener('keydown', this._onKeyDown);
    this.target.removeEventListener('keyup', this._onKeyUp);
    window.removeEventListener('blur', this._onBlur);
    this.target = null;
  }

  _isTypingTarget(event) {
    const el = event.target;
    if (!el || !el.tagName) return false;
    const tag = el.tagName.toLowerCase();
    return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
  }

  _onKeyDown(event) {
    if (this._isTypingTarget(event)) return;

    const action = ACTION_KEYMAP[event.code];
    if (action) {
      event.preventDefault();
      this._keyHeldActions.add(action);
      return;
    }

    // Hotkeys are handled before the pad map so a hotkey key is never also
    // delivered as a button press.
    const hotkey = HOTKEYS[event.code];
    if (hotkey && !event.repeat) {
      // Let the browser's own accelerators through (Ctrl+R, Cmd+S, ...).
      if (!event.ctrlKey && !event.metaKey && !event.altKey) {
        event.preventDefault();
        this.onHotkey?.(hotkey, event);
        return;
      }
    }

    const button = this.keymap[event.code];
    if (button === undefined) return;

    event.preventDefault();
    if (event.repeat) return; // autorepeat would re-send an already-held button

    this.keyboardState.set(button, true);
    this._flushPort(0);
  }

  _onKeyUp(event) {
    if (this._isTypingTarget(event)) return;

    const action = ACTION_KEYMAP[event.code];
    if (action) {
      event.preventDefault();
      this._keyHeldActions.delete(action);
      return;
    }

    const button = this.keymap[event.code];
    if (button === undefined) return;
    event.preventDefault();
    this.keyboardState.set(button, false);
    this._flushPort(0);
  }

  _onBlur() {
    this.keyboardState.clear();
    this.gamepadState.clear();
    this.touchState.clear();
    this.macroState.clear();
    this.lastSent.clear();
    this._keyHeldActions.clear();
    this._heldActions.clear();
    this._touchHeldActions.clear();
    this._cancelMacroPlayback();
    this._recording = null;
    this.core.clearInput();
  }

  /**
   * Called by touch-controls.js on pointerdown/pointerup for an on-screen
   * SNES button. Event-driven like keyboard input, not polled -- flushes
   * immediately rather than waiting for the next pollGamepads() call.
   */
  setTouchButton(buttonId, pressed) {
    this.touchState.set(buttonId, pressed);
    this._flushPort(0);
  }

  /** Same as setTouchButton() but for a host-level action (e.g. an on-screen fast-forward button). */
  setTouchAction(name, pressed) {
    if (pressed) this._touchHeldActions.add(name);
    else this._touchHeldActions.delete(name);
  }

  /** Release every on-screen-touch-held button/action -- call if the touch overlay is hidden mid-press. */
  clearTouchInput() {
    this.touchState.clear();
    this._touchHeldActions.clear();
    this._flushPort(0);
  }

  /**
   * Poll connected gamepads. Gamepad objects are snapshots, so getGamepads()
   * must be re-read every frame -- caching the array gives stale button state.
   */
  pollGamepads() {
    if (!navigator.getGamepads) return;

    const pads = navigator.getGamepads();
    const seenPorts = new Set();
    this._heldActions.clear();

    let port = 0;
    let primaryRaw = null;
    let primaryAxes = null;

    for (const pad of pads) {
      if (!pad || !pad.connected) continue;
      if (port >= 2) break; // two SNES ports is enough for now

      seenPorts.add(port);
      const raw = pad.buttons.map((b) => b?.pressed ?? false);
      const axes = pad.axes ?? [];
      if (port === 0) {
        primaryRaw = raw;
        primaryAxes = axes;
      }

      // Modifier only applies to the primary pad -- a second player's
      // controller doesn't get to reinterpret player 1's button meanings.
      const modifierHeld = port === 0 && this._isModifierPressed(raw, axes);

      for (const [index, joypadId] of this._buttonByIndex) {
        const pressed = raw[index] === true;
        if (modifierHeld && index !== this.modifierBinding) {
          const modBinding = this.modifiedBindings[JOYPAD_ID_TO_NAME[joypadId]];
          if (modBinding) {
            this._handleModifiedCombo(`b${index}`, pressed, modBinding);
            continue; // suppressed: this button's normal SNES press does not fire while held this way
          }
        }
        this.gamepadState.set(`${port}:${joypadId}`, pressed);
      }
      for (const { axisIndex, sign, joypadId } of this._axisButtonBindings) {
        const v = axes[axisIndex] ?? 0;
        const pressed = sign === '+' ? v > AXIS_DEADZONE : v < -AXIS_DEADZONE;
        const comboKey = `a${axisIndex}${sign}`;
        if (modifierHeld && comboKey !== this.modifierBinding) {
          const modBinding = this.modifiedBindings[JOYPAD_ID_TO_NAME[joypadId]];
          if (modBinding) {
            this._handleModifiedCombo(comboKey, pressed, modBinding);
            continue;
          }
        }
        this.gamepadState.set(`${port}:${joypadId}`, pressed);
      }

      // Extra triggers: modifier combos on a physical control that isn't
      // bound to any SNES button OR host action at all (e.g. a spare stick
      // button no game control needs). There's no "normal" press to fall
      // back to/suppress here, so unlike the two loops above, this only
      // ever does anything while the modifier is actually held.
      if (modifierHeld) {
        for (let index = 0; index < raw.length; index++) {
          if (index === this.modifierBinding) continue;
          if (this._buttonByIndex.has(index) || this._actionByIndex.has(index)) continue;
          const modBinding = this.extraBindings[index];
          if (modBinding) this._handleModifiedCombo(`b${index}`, raw[index] === true, modBinding);
        }
        for (let axisIndex = 2; axisIndex < axes.length; axisIndex++) {
          for (const sign of ['+', '-']) {
            const value = encodeAxisBinding(axisIndex, sign);
            if (value === this.modifierBinding) continue;
            const boundElsewhere =
              this._axisButtonBindings.some((b) => b.axisIndex === axisIndex && b.sign === sign) ||
              this._axisActionBindings.some((b) => b.axisIndex === axisIndex && b.sign === sign);
            if (boundElsewhere) continue;
            const modBinding = this.extraBindings[value];
            if (!modBinding) continue;
            const v = axes[axisIndex] ?? 0;
            const pressed = sign === '+' ? v > AXIS_DEADZONE : v < -AXIS_DEADZONE;
            this._handleModifiedCombo(`a${axisIndex}${sign}`, pressed, modBinding);
          }
        }
      }

      // Actions (fast forward, ...) are global/meta, so only the primary pad
      // drives them -- a second player's controller shouldn't be able to
      // speed up the whole session by accident.
      if (port === 0) {
        for (const [index, action] of this._actionByIndex) {
          if (raw[index]) this._heldActions.add(action);
        }
        for (const { axisIndex, sign, action } of this._axisActionBindings) {
          const v = axes[axisIndex] ?? 0;
          if (sign === '+' ? v > AXIS_DEADZONE : v < -AXIS_DEADZONE) this._heldActions.add(action);
        }
      }

      // Left stick doubles as the d-pad. OR'd with the hat above so either
      // works, which is what players expect on pads that report both.
      const [ax = 0, ay = 0] = axes;
      const orAxis = (button, active) => {
        if (active) this.gamepadState.set(`${port}:${button}`, true);
      };
      orAxis(JOYPAD.LEFT, ax < -AXIS_DEADZONE);
      orAxis(JOYPAD.RIGHT, ax > AXIS_DEADZONE);
      orAxis(JOYPAD.UP, ay < -AXIS_DEADZONE);
      orAxis(JOYPAD.DOWN, ay > AXIS_DEADZONE);

      port++;
    }

    // Clear state for ports whose pad went away this frame.
    for (const key of [...this.gamepadState.keys()]) {
      const p = Number(key.split(':')[0]);
      if (!seenPorts.has(p)) this.gamepadState.delete(key);
    }

    // Remap capture: bind the target to the first newly-pressed button (or,
    // failing that, axis -- for triggers that only ever show up in axes[])
    // on the primary pad. Edge-triggered, so the button that opened the
    // "Listen" UI via a mouse click can't immediately self-bind.
    if (this._listenTarget && primaryRaw) {
      let captured = null;

      for (let i = 0; i < primaryRaw.length; i++) {
        if (primaryRaw[i] && !this._prevRaw[i]) {
          captured = i;
          break;
        }
      }

      // Axes 0/1 are skipped: that's the left stick, which already has a
      // fixed job (d-pad emulation, above) -- capturing it here would bind
      // whatever's being listened for to "tilt the movement stick".
      if (captured === null && primaryAxes) {
        for (let i = 2; i < primaryAxes.length; i++) {
          const prevV = this._prevAxes[i] ?? 0;
          const v = primaryAxes[i];
          if (v > AXIS_DEADZONE && prevV <= AXIS_DEADZONE) {
            captured = encodeAxisBinding(i, '+');
            break;
          }
          if (v < -AXIS_DEADZONE && prevV >= -AXIS_DEADZONE) {
            captured = encodeAxisBinding(i, '-');
            break;
          }
        }
      }

      if (captured !== null) this._resolveListening(captured);
    }
    if (primaryRaw) this._prevRaw = primaryRaw;
    if (primaryAxes) this._prevAxes = [...primaryAxes];

    this._flushPort(0);
    if (seenPorts.has(1)) this._flushPort(1);
  }

  /** Whether `this.modifierBinding`'s physical control is currently held, from this frame's raw pad state. */
  _isModifierPressed(raw, axes) {
    const value = this.modifierBinding;
    if (value === null || value === undefined) return false;
    if (isAxisBinding(value)) {
      const { axisIndex, sign } = parseAxisBinding(value);
      const v = axes[axisIndex] ?? 0;
      return sign === '+' ? v > AXIS_DEADZONE : v < -AXIS_DEADZONE;
    }
    return raw[value] === true;
  }

  /** Edge-triggered: fires `modBinding` once when a suppressed combo control transitions to pressed. */
  _handleModifiedCombo(comboKey, pressed, modBinding) {
    const wasPressed = this._prevComboRaw.get(comboKey) === true;
    this._prevComboRaw.set(comboKey, pressed);
    if (pressed && !wasPressed) this._fireModified(modBinding);
  }

  _fireModified(modBinding) {
    if (modBinding.type === 'action') this.onHotkey?.(modBinding.action);
    else if (modBinding.type === 'macro') this.playMacro(modBinding.events);
  }

  /** Merge keyboard + gamepad + on-screen touch + macro playback for one port and send only what changed. */
  _flushPort(port) {
    for (const button of Object.values(JOYPAD)) {
      const fromKeyboard = port === 0 && this.keyboardState.get(button) === true;
      const fromTouch = port === 0 && this.touchState.get(button) === true;
      const fromMacro = port === 0 && this.macroState.get(button) === true;
      const fromGamepad = this.gamepadState.get(`${port}:${button}`) === true;
      const pressed = fromKeyboard || fromTouch || fromMacro || fromGamepad;

      const key = `${port}:${button}`;
      if (this.lastSent.get(key) === pressed) continue;

      if (this._recording && port === 0) {
        this._recording.events.push({ t: Math.round(performance.now() - this._recording.startTime), buttonId: button, pressed });
      }

      this.lastSent.set(key, pressed);
      this.core.setInput(port, button, pressed ? 1 : 0);
    }
  }

  /** Any pad currently visible to the browser. */
  get gamepadCount() {
    if (!navigator.getGamepads) return 0;
    return [...navigator.getGamepads()].filter((p) => p && p.connected).length;
  }

  /** True if `name` (a REMAPPABLE_BUTTONS/ACTIONS entry) is currently held. */
  isActionHeld(name) {
    return this._keyHeldActions.has(name) || this._heldActions.has(name) || this._touchHeldActions.has(name);
  }

  /* ------------------------------------------------------- remapping -- */

  /** Snapshot of current bindings for rendering a remap UI. */
  getBindings() {
    return {
      buttons: { ...this.buttonBindings },
      actions: { ...this.actionBindings },
    };
  }

  /** @param {number|string} value a gamepad button index, or an axis binding from describeBinding()'s input space */
  setButtonBinding(name, value) {
    if (JOYPAD[name] === undefined) return;
    this._clearIndexFromAllBindings(value);
    this.buttonBindings[name] = value;
    this._rebuildLookups();
    this._persist();
  }

  /** @param {number|string} value */
  setActionBinding(name, value) {
    this._clearIndexFromAllBindings(value);
    this.actionBindings[name] = value;
    this._rebuildLookups();
    this._persist();
  }

  clearButtonBinding(name) {
    this.buttonBindings[name] = null;
    this._rebuildLookups();
    this._persist();
  }

  clearActionBinding(name) {
    this.actionBindings[name] = null;
    this._rebuildLookups();
    this._persist();
  }

  /* -------------------------------------------------- modifier + macros -- */

  getModifierBinding() {
    return this.modifierBinding;
  }

  setModifierBinding(value) {
    this.modifierBinding = value;
    this._prevComboRaw.clear();
    this._persistModifier();
  }

  clearModifierBinding() {
    this.modifierBinding = null;
    this._prevComboRaw.clear();
    this._persistModifier();
  }

  /** Snapshot of every configured modifier+button combo: SNES button name -> {type, action|events|label}. */
  getModifiedBindings() {
    return { ...this.modifiedBindings };
  }

  setModifiedAction(buttonName, action) {
    if (JOYPAD[buttonName] === undefined) return;
    this.modifiedBindings[buttonName] = { type: 'action', action };
    this._persistModifier();
  }

  /** @param {{t:number, buttonId:number, pressed:boolean}[]} events from stopRecordingMacro() */
  setModifiedMacro(buttonName, events, label) {
    if (JOYPAD[buttonName] === undefined) return;
    this.modifiedBindings[buttonName] = { type: 'macro', events, label: label ?? `Macro (${events.length} steps)` };
    this._persistModifier();
  }

  clearModifiedBinding(buttonName) {
    delete this.modifiedBindings[buttonName];
    this._persistModifier();
  }

  /**
   * Snapshot of every configured modifier+EXTRA-control combo: raw binding
   * value (button index or axis string, as a string key -- object keys are
   * always strings, see parseExtraKey()) -> {type, action|events|label}.
   */
  getExtraBindings() {
    return { ...this.extraBindings };
  }

  setExtraAction(value, action) {
    if (value === null || value === undefined) return;
    this.extraBindings[value] = { type: 'action', action };
    this._persistModifier();
  }

  /** @param {{t:number, buttonId:number, pressed:boolean}[]} events from stopRecordingMacro() */
  setExtraMacro(value, events, label) {
    if (value === null || value === undefined) return;
    this.extraBindings[value] = { type: 'macro', events, label: label ?? `Macro (${events.length} steps)` };
    this._persistModifier();
  }

  clearExtraBinding(value) {
    delete this.extraBindings[value];
    this._persistModifier();
  }

  resetModifierBindings() {
    this.cancelListening();
    this._cancelMacroPlayback();
    this.modifierBinding = null;
    this.modifiedBindings = {};
    this.extraBindings = {};
    this._prevComboRaw.clear();
    try {
      localStorage.removeItem(MODIFIER_STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
  }

  /** Start capturing a button-press sequence (from any input source) as a macro. Call stopRecordingMacro() to finish. */
  startRecordingMacro() {
    this._recording = { startTime: performance.now(), events: [] };
  }

  get isRecordingMacro() {
    return this._recording !== null;
  }

  /** Live snapshot of the in-progress recording, for a "here's what's been captured so far" UI. Null if not recording. */
  getRecordingEvents() {
    return this._recording ? [...this._recording.events] : null;
  }

  /** @returns {{t:number, buttonId:number, pressed:boolean}[]} the recorded events, or [] if nothing was recorded */
  stopRecordingMacro() {
    const events = this._recording?.events ?? [];
    this._recording = null;
    return events;
  }

  /** Play back a recorded event sequence through the same input pipeline as any other source. Cancels any macro already playing. */
  playMacro(events) {
    this._cancelMacroPlayback();
    if (!events || events.length === 0) return;

    for (const ev of events) {
      const timer = setTimeout(() => {
        this.macroState.set(ev.buttonId, ev.pressed);
        this._flushPort(0);
      }, ev.t);
      this._macroTimers.push(timer);
    }

    // Safety net: a cleanly-recorded macro already ends with every button
    // released, but clear unconditionally shortly after the last event in
    // case it doesn't (e.g. recording stopped mid-press).
    const lastT = events[events.length - 1].t;
    this._macroTimers.push(
      setTimeout(() => {
        this.macroState.clear();
        this._flushPort(0);
      }, lastT + 50),
    );
  }

  _cancelMacroPlayback() {
    for (const timer of this._macroTimers) clearTimeout(timer);
    this._macroTimers = [];
  }

  _persistModifier() {
    try {
      localStorage.setItem(
        MODIFIER_STORAGE_KEY,
        JSON.stringify({ modifier: this.modifierBinding, modified: this.modifiedBindings, extra: this.extraBindings }),
      );
    } catch {
      /* storage unavailable or full */
    }
  }

  resetBindings() {
    this.cancelListening();
    this.buttonBindings = { ...DEFAULT_BUTTON_BINDINGS };
    this.actionBindings = { ...DEFAULT_ACTION_BINDINGS };
    this._rebuildLookups();
    try {
      localStorage.removeItem(GAMEPAD_MAP_STORAGE_KEY);
    } catch {
      /* storage unavailable */
    }
  }

  /**
   * Wait for the next gamepad button press (or, for a trigger that only
   * shows up in axes[], the next significant axis deflection) and bind it
   * to `target`. Resolves with the captured value (see describeBinding()),
   * or null if cancelled or timed out (nothing pressed within `timeoutMs`).
   *
   * @param {{kind: 'button'|'action'|'modifier'|'extra', name?: string}} target `name` is unused (and unnecessary) for kind 'modifier'/'extra' -- there's only one modifier, and an 'extra' capture just hands back the raw value for the caller to attach an action/macro to itself (see setExtraAction/setExtraMacro), rather than auto-binding anything.
   * @param {{timeoutMs?: number}} [opts]
   * @returns {Promise<number|string|null>}
   */
  startListening(target, { timeoutMs = 8000 } = {}) {
    this.cancelListening();
    return new Promise((resolve) => {
      this._listenTarget = target;
      this._listenResolve = resolve;
      this._listenTimer = setTimeout(() => this.cancelListening(), timeoutMs);
    });
  }

  cancelListening() {
    if (this._listenTimer) clearTimeout(this._listenTimer);
    this._listenTimer = null;
    const resolve = this._listenResolve;
    this._listenTarget = null;
    this._listenResolve = null;
    resolve?.(null);
  }

  _resolveListening(value) {
    const target = this._listenTarget;
    const resolve = this._listenResolve;
    if (this._listenTimer) clearTimeout(this._listenTimer);
    this._listenTimer = null;
    this._listenTarget = null;
    this._listenResolve = null;

    if (target.kind === 'button') this.setButtonBinding(target.name, value);
    else if (target.kind === 'modifier') this.setModifierBinding(value);
    else if (target.kind === 'action') this.setActionBinding(target.name, value);
    // kind 'extra': nothing to auto-bind -- the resolved value itself
    // becomes a fresh modifier-combo target once the caller attaches an
    // action or macro to it (setExtraAction/setExtraMacro).

    resolve?.(value);
  }

  /** A physical control (button index or axis binding) can only be bound to one thing; unbind anything else that used it. */
  _clearIndexFromAllBindings(value) {
    if (value === null || value === undefined) return;
    for (const name of Object.keys(this.buttonBindings)) {
      if (this.buttonBindings[name] === value) this.buttonBindings[name] = null;
    }
    for (const name of Object.keys(this.actionBindings)) {
      if (this.actionBindings[name] === value) this.actionBindings[name] = null;
    }
  }

  _rebuildLookups() {
    this._buttonByIndex = new Map(); // gamepad button index -> JOYPAD id
    this._axisButtonBindings = []; // [{axisIndex, sign, joypadId}]
    for (const [name, value] of Object.entries(this.buttonBindings)) {
      if (value === null || value === undefined || JOYPAD[name] === undefined) continue;
      if (isAxisBinding(value)) {
        this._axisButtonBindings.push({ ...parseAxisBinding(value), joypadId: JOYPAD[name] });
      } else {
        this._buttonByIndex.set(value, JOYPAD[name]);
      }
    }

    this._actionByIndex = new Map(); // gamepad button index -> action name
    this._axisActionBindings = []; // [{axisIndex, sign, action}]
    for (const [name, value] of Object.entries(this.actionBindings)) {
      if (value === null || value === undefined) continue;
      if (isAxisBinding(value)) {
        this._axisActionBindings.push({ ...parseAxisBinding(value), action: name });
      } else {
        this._actionByIndex.set(value, name);
      }
    }
  }

  /** Raw per-frame state of the primary connected pad, for a diagnostics UI. Null if none connected. */
  getPrimaryPadDebugState() {
    if (!navigator.getGamepads) return null;
    for (const pad of navigator.getGamepads()) {
      if (!pad || !pad.connected) continue;
      return {
        id: pad.id,
        mapping: pad.mapping || '(none -- non-standard layout)',
        buttons: pad.buttons.map((b) => (b?.pressed ? 1 : Number((b?.value ?? 0).toFixed(2)))),
        axes: (pad.axes ?? []).map((a) => Number(a.toFixed(2))),
      };
    }
    return null;
  }

  _persist() {
    try {
      localStorage.setItem(
        GAMEPAD_MAP_STORAGE_KEY,
        JSON.stringify({ buttons: this.buttonBindings, actions: this.actionBindings }),
      );
    } catch {
      /* storage unavailable or full: bindings just won't survive a reload */
    }
  }
}

/** Keys reserved for the frontend rather than the emulated pad. */
const HOTKEYS = {
  F2: 'saveState',
  F4: 'loadState',
  F5: 'saveState',
  F8: 'loadState',
  Escape: 'pause',
  Space: 'pause',
  KeyR: 'reset',
  KeyM: 'mute',
  KeyF: 'fullscreen',
  Digit1: 'slot1',
  Digit2: 'slot2',
  Digit3: 'slot3',
  Digit4: 'slot4',
};
